import { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { BannerStyle } from '../../../../typings/enums';
import { useAddToast } from '../../../contexts/ToastContext';
import { useEditable } from '../../../hooks';
import useGetLocator from '../../../hooks/api/locator/useGetLocator';
import { useCreateMalwareReport, useUpdateMalwareReport } from '../../../hooks/api/malware/index';
import Banner from '../../generic/Banner';
import Button, { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, InlineLoaderButton } from '../../generic/Button';
import LabeledCheckbox from '../../generic/Forms/LabeledCheckbox';
import LabeledTextArea from '../../generic/Forms/LabeledTextArea';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import List from '../../generic/List';
import type { MalwareReport } from '../../../../shared/model-typings/malwarereport';

import styles from './ModalMalware.module.less';

type MalwareFormData = {
  name: string;
  url: string;
  description: string;
  selectedLocators: Set<string>;
};

type ModalMalwareProps = {
  show: boolean;
  onHide: () => void;
  malwareReport?: MalwareReport;
};

const ModalMalware: VoidFunctionComponent<ModalMalwareProps> = ({
  show,
  onHide,
  malwareReport,
}) => {
  const addToast = useAddToast();
  const createMalwareReportMutation = useCreateMalwareReport();
  const updateMalwareReportMutation = useUpdateMalwareReport();

  const [searchQuery, setSearchQuery] = useState('');
  const searchResults = useGetLocator(searchQuery);

  const initialFormData = useMemo<MalwareFormData>(() => {
    return {
      name: malwareReport?.name || '',
      url: malwareReport?.url || '',
      description: malwareReport?.description || '',
      selectedLocators: new Set(malwareReport?.malwareRevisions
        ?.map((revision) => revision.dependencyRevisionLocator) || []),
    };
  }, [malwareReport]);

  const { data: formData, onUpdate, onReset, onSave, isDirty } = useEditable(initialFormData);

  const handleLocatorToggle = (locator: string) => {
    const newSelectedLocators = new Set(formData.selectedLocators);
    if (newSelectedLocators.has(locator)) {
      newSelectedLocators.delete(locator);
    } else {
      newSelectedLocators.add(locator);
    }
    onUpdate({ selectedLocators: newSelectedLocators });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const onModalHide = () => {
    onReset();
    setSearchQuery('');
    onHide();
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.url || !formData.description || formData.selectedLocators.size === 0) {
      addToast({
        type: 'error',
        content: 'Please fill in all required fields and select at least one locator.',
      });
      return;
    }
    const malwareData = {
      name: formData.name,
      url: formData.url,
      description: formData.description,
      locators: Array.from(formData.selectedLocators),
    };

    if (malwareReport?.id) {
      await updateMalwareReportMutation.mutateAsync({
        id: malwareReport.id,
        malwareData,
      });
    } else {
      await createMalwareReportMutation.mutateAsync(malwareData);
    }

    // Success toast is handled by the mutation hooks
    onSave();
    onModalHide();
  };

  const isLoading = createMalwareReportMutation.isPending || updateMalwareReportMutation.isPending;

  return (
    <Modal className="modal-malware" onHide={onModalHide} animation show={show}>
      <Modal.Header closeButton>
        <Modal.Title>
          {malwareReport?.id ? 'Edit' : 'Add New'} Malware Report
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="margin-bottom-lg">
          <Banner
            title=""
            body="The information you enter here will be visible to customers when they view malware issues. Please ensure all details are accurate, professional, and clear."
            bannerStyle={BannerStyle.Info}
          />
        </div>
        <div className="margin-bottom-md">
          <LabeledTextInput
            labelVariant="legacy"
            label="Name"
            value={formData.name}
            onChange={(value) => onUpdate({ name: value })}
            placeholder="Enter malware name"
            isRequired
          />
        </div>
        <div className="margin-bottom-md">
          <LabeledTextInput
            labelVariant="legacy"
            label="URL"
            value={formData.url}
            onChange={(value) => onUpdate({ url: value })}
            placeholder="https://example.com/malware-report"
            isRequired
          />
        </div>
        <div className="margin-bottom-md">
          <LabeledTextArea
            labelVariant="legacy"
            label="Description"
            value={formData.description}
            onChange={(value) => onUpdate({ description: value })}
            placeholder="Enter detailed description of the malware"
            isRequired
          />
        </div>
        <div className="margin-bottom-md">
          <LabeledTextInput
            labelVariant="legacy"
            label="Search Locators"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Enter exact locator (e.g., npm+react$18.2.0)"
          />
          {searchResults.isLoading && searchResults.isFetching && <div className="margin-top-xs">Searching...</div>}

          {/* Search Results */}
          {searchQuery && searchResults.data && (
            <List>
              <List.Header>
                <List.HeaderText>
                  <strong>Search Results</strong>
                </List.HeaderText>
              </List.Header>
              <List.Body>
                <List.RowContainer>
                  <div className={styles.listBody}>
                    {searchResults.data.length > 0 ? (
                      <List.Row hasTightPadding isInteractive={false}>
                        {searchResults.data.map((result) => (
                          <div key={result.locator} className="margin-bottom-xs width-full">
                            <LabeledCheckbox
                              label={result.locator}
                              isChecked={formData.selectedLocators.has(result.locator)}
                              onChange={() => handleLocatorToggle(result.locator)}
                            />
                          </div>
                        ))}
                      </List.Row>
                    ) : (
                      <div className="padding-md text-center text-muted">
                        No locator found
                      </div>
                    )}
                  </div>
                </List.RowContainer>
              </List.Body>
            </List>
          )}

          {/* Selected Locators */}
          {formData.selectedLocators.size > 0 && (
            <List>
              <List.Header>
                <List.HeaderText>
                  <strong>Selected Locators ({formData.selectedLocators.size})</strong>
                </List.HeaderText>
              </List.Header>
              <List.Body>
                <List.RowContainer>
                  <div className={styles.listBody}>
                    <List.Row hasTightPadding isInteractive={false}>
                      {Array.from(formData.selectedLocators).map((locator) => (
                        <div key={locator} className="margin-bottom-xs width-full">
                          <LabeledCheckbox
                            label={locator}
                            isChecked
                            onChange={() => handleLocatorToggle(locator)}
                        />
                        </div>
                      ))}
                    </List.Row>
                  </div>
                </List.RowContainer>
              </List.Body>
            </List>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type={BUTTON_TYPE.SECONDARY}
            size={BUTTON_SIZE.LARGE}
            isDisabled={isLoading}
            onClick={onModalHide}
          >
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.LARGE}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isLoading || !isDirty}
            onClick={handleSubmit}
          >
            {malwareReport?.id ? 'Update Report' : 'Create Report'}
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalMalware;
